import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const POLL_MS = 20000;
export const ALERT_MUTE_KEY = "tm_seller_alert_muted";

let sharedCtx = null;
function unlockAudio() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx) sharedCtx = new AC();
  if (sharedCtx.state === "suspended") sharedCtx.resume().catch(() => {});
  return sharedCtx;
}

function playChime() {
  const ctx = unlockAudio();
  if (!ctx || ctx.state !== "running") return;
  const now = ctx.currentTime;
  [880, 1174.66, 1567.98].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const start = now + i * 0.16;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.28, start + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.55);
  });
}

// Headless: polls the seller's orders and alerts (chime + toast) on new "placed" orders.
export default function SellerOrderAlerts() {
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role !== "seller" || !user?.id) return;

    // Unlock the audio context on the first user gesture (browsers block autoplay).
    const onGesture = () => unlockAudio();
    window.addEventListener("pointerdown", onGesture, { once: true });
    window.addEventListener("keydown", onGesture, { once: true });

    const storeKey = `tm_seen_orders_${user.id}`;
    const nudgedKey = `tm_lowstock_nudged_${user.id}`;
    const oosNudgedKey = `tm_oos_nudged_${user.id}`;
    const raw = localStorage.getItem(storeKey);
    const seen = new Set(raw ? JSON.parse(raw) : []);
    let nudged = new Set(JSON.parse(localStorage.getItem(nudgedKey) || "[]"));
    let oosNudged = new Set(JSON.parse(localStorage.getItem(oosNudgedKey) || "[]"));
    let isFresh = raw === null; // brand-new device/session → don't alert for the existing backlog
    let stopped = false;

    const persist = () => localStorage.setItem(storeKey, JSON.stringify([...seen].slice(-2000)));
    const persistNudged = () => {
      localStorage.setItem(nudgedKey, JSON.stringify([...nudged].slice(-2000)));
      localStorage.setItem(oosNudgedKey, JSON.stringify([...oosNudged].slice(-2000)));
    };

    const checkLowStock = async () => {
      try {
        const { data: prods } = await api.get("/seller/products");
        if (stopped || !Array.isArray(prods)) return;
        const lowIds = new Set(prods.filter((p) => p.low_stock).map((p) => p.id));
        const zeroIds = new Set(prods.filter((p) => (p.stock ?? 0) <= 0).map((p) => p.id));
        if (isFresh) {
          nudged = new Set([...lowIds]);
          oosNudged = new Set([...zeroIds]);
          persistNudged();
          return;
        }
        [...nudged].forEach((pid) => { if (!lowIds.has(pid)) nudged.delete(pid); });
        [...oosNudged].forEach((pid) => { if (!zeroIds.has(pid)) oosNudged.delete(pid); });
        const newLow = prods.filter((p) => p.low_stock && (p.stock ?? 0) > 0 && !nudged.has(p.id));
        const newZero = prods.filter((p) => (p.stock ?? 0) <= 0 && !oosNudged.has(p.id));
        prods.filter((p) => p.low_stock).forEach((p) => nudged.add(p.id));
        newZero.forEach((p) => oosNudged.add(p.id));
        persistNudged();
        const alerts = [
          ...newZero.map((p) => ({ p, zero: true })),
          ...newLow.map((p) => ({ p, zero: false })),
        ];
        if (alerts.length > 0) {
          if (localStorage.getItem(ALERT_MUTE_KEY) !== "1") playChime();
          alerts.slice(0, 3).forEach(({ p, zero }) => {
            if (zero) {
              toast.error(`Out of stock: ${p.title}`, { description: "Restock to keep selling this item.", duration: 9000 });
            } else {
              toast(`Low stock: ${p.title} (${p.stock} left)`, { description: "Consider restocking soon.", duration: 9000 });
            }
          });
        }
      } catch (e) { /* ignore */ }
    };

    const poll = async () => {
      try {
        const { data } = await api.get("/seller/orders");
        if (stopped || !Array.isArray(data)) return;
        if (isFresh) {
          data.forEach((o) => seen.add(o.id));
          persist();
          await checkLowStock();
          isFresh = false;
          return;
        }
        const fresh = data.filter((o) => o.status === "placed" && !seen.has(o.id));
        data.forEach((o) => seen.add(o.id));
        persist();
        if (fresh.length > 0) {
          if (localStorage.getItem(ALERT_MUTE_KEY) !== "1") playChime();
          toast.success(
            fresh.length === 1 ? "New order received!" : `${fresh.length} new orders received!`,
            { description: "Open your Orders queue to process it.", duration: 8000 }
          );
        }
        await checkLowStock();
      } catch (e) { /* ignore transient network errors */ }
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, [user]);

  return null;
}
